// ============================================
// Zen-AI-Pentest Platform - Neural Background
// Animated Neural Network Visualization
// ============================================

import { useEffect, useRef, useState } from 'react';

interface NeuralBackgroundProps {
  intensity?: 'low' | 'medium' | 'high';
  color?: string;
}

export function NeuralBackground({ intensity = 'medium', color = '#6366f1' }: NeuralBackgroundProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isVisible, setIsVisible] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    // Check if document is visible (for performance)
    const handleVisibilityChange = () => {
      setIsVisible(!document.hidden);
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    if (!isVisible || hasError) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    let ctx: CanvasRenderingContext2D | null = null;
    try {
      ctx = canvas.getContext('2d');
      if (!ctx) {
        setHasError(true);
        return;
      }
    } catch (e) {
      console.warn('Canvas not supported:', e);
      setHasError(true);
      return;
    }

    let animationId: number;
    let particles: Array<{
      x: number;
      y: number;
      vx: number;
      vy: number;
      radius: number;
      connections: number[];
    }> = [];

    const config = {
      low: { particleCount: 15, connectionDistance: 100, speed: 0.3 },
      medium: { particleCount: 25, connectionDistance: 150, speed: 0.5 },
      high: { particleCount: 40, connectionDistance: 200, speed: 0.7 },
    }[intensity];

    function resize() {
      if (!canvas) return;
      const parent = canvas.parentElement;
      if (parent) {
        canvas.width = parent.clientWidth;
        canvas.height = parent.clientHeight;
      } else {
        canvas.width = window.innerWidth;
        canvas.height = window.innerHeight;
      }
    }

    function initParticles() {
      if (!canvas) return;
      particles = [];
      for (let i = 0; i < config.particleCount; i++) {
        particles.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          vx: (Math.random() - 0.5) * config.speed,
          vy: (Math.random() - 0.5) * config.speed,
          radius: Math.random() * 2 + 1,
          connections: [],
        });
      }
    }

    let frameCount = 0;
    function drawParticles() {
      if (!ctx || !canvas || !isVisible) return;
      
      // Render every 2nd frame for performance (30fps instead of 60fps)
      frameCount++;
      if (frameCount % 2 !== 0) {
        animationId = requestAnimationFrame(drawParticles);
        return;
      }

      const context = ctx;
      const canvasEl = canvas;
      
      context.clearRect(0, 0, canvasEl.width, canvasEl.height);

      // Update particle positions
      particles.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;

        // Bounce off edges
        if (p.x < 0 || p.x > canvasEl.width) p.vx *= -1;
        if (p.y < 0 || p.y > canvasEl.height) p.vy *= -1;

        // Keep within bounds
        p.x = Math.max(0, Math.min(canvasEl.width, p.x));
        p.y = Math.max(0, Math.min(canvasEl.height, p.y));
      });

      // Find connections (only check every 5th particle for performance)
      particles.forEach((p1, i) => {
        if (i % 5 !== 0) return;
        p1.connections = [];
        particles.forEach((p2, j) => {
          if (i === j) return;
          const dx = p1.x - p2.x;
          const dy = p1.y - p2.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          if (distance < config.connectionDistance) {
            p1.connections.push(j);
          }
        });
      });

      // Draw connections
      particles.forEach((p1) => {
        p1.connections.forEach((j) => {
          const p2 = particles[j];
          const dx = p1.x - p2.x;
          const dy = p1.y - p2.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          const opacity = 1 - distance / config.connectionDistance;

          context.beginPath();
          context.moveTo(p1.x, p1.y);
          context.lineTo(p2.x, p2.y);
          context.strokeStyle = `${color}${Math.floor(opacity * 20).toString(16).padStart(2, '0')}`;
          context.lineWidth = 0.5;
          context.stroke();
        });
      });

      // Draw particles
      particles.forEach((p) => {
        context.beginPath();
        context.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
        context.fillStyle = `${color}30`;
        context.fill();
      });

      animationId = requestAnimationFrame(drawParticles);
    }

    try {
      resize();
      initParticles();
      drawParticles();
    } catch (e) {
      console.warn('Animation error:', e);
      setHasError(true);
    }

    const handleResize = () => {
      try {
        resize();
        initParticles();
      } catch (e) {
        console.warn('Resize error:', e);
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener('resize', handleResize);
    };
  }, [intensity, color, isVisible, hasError]);

  // If canvas fails, show CSS fallback
  if (hasError) {
    return (
      <div 
        className="fixed inset-0 pointer-events-none z-0"
        style={{
          background: `
            radial-gradient(circle at 20% 30%, ${color}10 0%, transparent 50%),
            radial-gradient(circle at 80% 70%, ${color}08 0%, transparent 50%),
            radial-gradient(circle at 50% 50%, ${color}05 0%, transparent 70%)
          `,
        }}
      />
    );
  }

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none z-0"
      style={{ 
        opacity: 0.4,
        width: '100%',
        height: '100%',
      }}
    />
  );
}
